/**
 * Keyboard + pointer-lock mouse input, and the seam every other input device
 * feeds through.
 *
 * `InputAPI` is deliberately unchanged: the player controller, the survival
 * system and the UI ask the same six questions they always have, and none of
 * them can tell whether the answer came from a key, a mouse, a thumb on glass
 * or a controller. Anything that is not the keyboard writes through
 * `InputSink` below and lands in exactly the same `down` / `justPressed` /
 * `lookX` / `moveX` state.
 *
 * The touch path is additive and gated. A machine with no touch hardware adds
 * no listener, allocates no object, takes one null check per frame and never
 * fetches the module that draws the on-screen controls — it is a separate
 * chunk behind a dynamic import that only a touch device ever reaches. This
 * file does not perform that import: `core/` is beneath `ui/`, so the owner
 * supplies a `TouchLayerFactory` and this file only decides when to call it.
 */

import { TUNING } from './config';
import { forcedTouchMode, hasTouchHardware, touchPreferred, watchTouchPreference } from './touch';
import type { ActionName, InputAPI } from './types';

const DEFAULT_BINDINGS: Record<string, ActionName> = {
  KeyW: 'forward',
  ArrowUp: 'forward',
  KeyS: 'back',
  ArrowDown: 'back',
  KeyA: 'left',
  ArrowLeft: 'left',
  KeyD: 'right',
  ArrowRight: 'right',
  ShiftLeft: 'sprint',
  ShiftRight: 'sprint',
  ControlLeft: 'crouch',
  KeyC: 'crouch',
  Space: 'jump',
  KeyE: 'interact',
  KeyV: 'attack',
  KeyF: 'flashlight',
  Tab: 'inventory',
  KeyM: 'map',
  Escape: 'pause',
  Digit1: 'use1',
  Digit2: 'use2',
  Digit3: 'use3',
  Digit4: 'use4',
};

/**
 * Milliseconds to wait for an answer about the pointer before deciding one from
 * `document.pointerLockElement`. Comfortably longer than the second or so
 * Chrome blocks the request for after the user pressed Escape themselves, and
 * short enough that a resume which is going to fail fails while the player is
 * still looking at the screen it failed on.
 */
const LOCK_TIMEOUT_MS = 1200;

/**
 * The write side of the input manager.
 *
 * Anything that is not the keyboard and mouse — the on-screen touch controls,
 * and an XR controller when there is one — drives the game through this rather
 * than through a parallel path of its own. Everything it writes ends up in the
 * same fields `InputAPI` reads, which is the whole integration strategy: no
 * subsystem downstream is aware that another input device exists.
 */
export interface InputSink {
  /**
   * Radians of look per pixel of drag, as the sensitivity setting currently
   * has it. A source that works in pixels multiplies by this so the settings
   * slider keeps meaning something.
   */
  readonly lookScale: number;
  /**
   * Analogue movement in -1..1, latched until it is set again. Overrides the
   * keyboard on whichever axis is non-zero; `axes(0, 0)` hands it back.
   */
  axes(x: number, z: number): void;
  /** Radians of look to add to this frame. `dy` is pre-invert. */
  look(dx: number, dy: number): void;
  /** Hold or release an action for as long as the source says so. */
  hold(action: ActionName, down: boolean): void;
  /**
   * A press that lasts exactly one frame: `pressed()` is true on the next
   * tick and `isDown()` with it, which is what a mouse click looks like to
   * everything that reads them.
   */
  tap(action: ActionName): void;
}

/**
 * The on-screen controls, as this file needs to be able to talk to them.
 *
 * It does not build them. `core/` is underneath `ui/` and may not reach up into
 * it — not even through a dynamic `import()`, which is a runtime edge a desktop
 * never walks but is still `core` naming a module in `ui`. So the owner (which
 * is `game.ts`, the one file that knows about both) hands in a factory, and
 * this file only decides *when* to call it.
 */
export interface TouchLayer {
  /** Drop anything the thumbs are holding. */
  release(): void;
  dispose(): void;
}

/**
 * Build the on-screen controls against this sink.
 *
 * Asynchronous because the module that draws them should be a chunk a desktop
 * never fetches, and the owner's implementation is therefore a dynamic
 * `import()`. Resolving to null means "could not" and is not an error: no
 * controls is bad, and a rejected module that takes the game's constructor down
 * with it is worse.
 */
export type TouchLayerFactory = (sink: InputSink) => Promise<TouchLayer | null>;

class Input implements InputAPI, InputSink {
  private down = new Set<ActionName>();
  private justPressed = new Set<ActionName>();
  private bindings = { ...DEFAULT_BINDINGS };
  private sensitivity = TUNING.player.sensitivity;
  private invertY = false;
  private canvas: HTMLElement;

  lookX = 0;
  lookY = 0;
  moveX = 0;
  moveZ = 0;
  locked = false;

  /**
   * Analogue axes from a non-keyboard source, or null when there has never
   * been one. Null is the desktop case and costs `beginFrame` a single null
   * check; the object is allocated once, at the first touch, and reused.
   */
  private ext: { x: number; z: number } | null = null;
  /** Actions a source tapped this frame, released again by `endFrame`. */
  private readonly taps: ActionName[] = [];

  /** True while the on-screen controls are the ones driving the game. */
  private touchMode = false;
  /** Non-null only in touch mode. */
  private touch: TouchLayer | null = null;
  /**
   * Bumped on every mode change so a HUD whose module arrives after the mode
   * has flipped back is thrown away rather than installed over a desktop.
   */
  private touchToken = 0;
  private unwatchTouch: (() => void) | null = null;
  /**
   * Whether this machine could ever want the touch path. Decided once, at
   * construction: a desktop that answers no here installs no listener, watches
   * no media query and never evaluates `touchPreferred` again.
   */
  private readonly touchCapable: boolean;
  /**
   * What the player last actually touched the machine with. Only ever written
   * on a touch-capable machine, and only read when a lock request fails — see
   * `answerLock`.
   */
  private lastPointerType = '';
  /**
   * Builds the on-screen controls when they are wanted. Only ever stored on a
   * machine that could want them, so a desktop holds no reference to the layer
   * above it and nothing can accidentally fetch the module.
   */
  private makeTouchLayer: TouchLayerFactory | null = null;

  private onKeyDown = (e: KeyboardEvent) => {
    // Tab and Escape would otherwise leave the canvas or blur focus.
    const action = this.bindings[e.code];
    if (action) {
      if (e.code === 'Tab') e.preventDefault();
      if (!e.repeat) {
        this.down.add(action);
        this.justPressed.add(action);
      }
    }
  };

  private onKeyUp = (e: KeyboardEvent) => {
    const action = this.bindings[e.code];
    if (action) this.down.delete(action);
  };

  private onMouseMove = (e: MouseEvent) => {
    if (!this.locked) return;
    this.lookX += e.movementX * this.sensitivity;
    this.lookY += e.movementY * this.sensitivity * (this.invertY ? -1 : 1);
  };

  private onMouseDown = (e: MouseEvent) => {
    if (!this.locked) return;
    // Left click doubles as interact so the game is playable one-handed.
    if (e.button === 0) {
      this.down.add('interact');
      this.justPressed.add('interact');
    }
    // Right, held, is the strike wind-up. `preventDefault` stops the context
    // menu, which would otherwise take the pointer lock with it mid-swing.
    if (e.button === 2) {
      e.preventDefault();
      this.down.add('attack');
      this.justPressed.add('attack');
    }
  };

  private onMouseUp = (e: MouseEvent) => {
    if (e.button === 0) this.down.delete('interact');
    if (e.button === 2) this.down.delete('attack');
  };

  /** Only while locked; outside the game the browser's own menu is nobody's business. */
  private onContextMenu = (e: MouseEvent) => {
    if (this.locked) e.preventDefault();
  };

  /**
   * True while a lock release the game asked for is still in flight. Losing the
   * lock is normally the player pressing Escape, which should open the pause
   * menu — but `pause()`, `quit` and death all release the lock themselves, and
   * synthesising a press for those would immediately toggle the menu back off
   * on the very next frame.
   */
  private selfReleasing = false;

  private onLockChange = () => {
    this.locked = document.pointerLockElement === this.canvas;
    if (!this.locked) {
      // Never leave a key stuck down when focus is lost.
      this.clearHeld();
      if (this.selfReleasing) this.selfReleasing = false;
      else this.justPressed.add('pause');
    }
  };

  private onBlur = () => {
    this.clearHeld();
  };

  /**
   * Let go of everything.
   *
   * The touch layer has to be told rather than just having `down` emptied
   * underneath it: the crouch button is a latch, and a latch whose action has
   * been cleared behind its back is a lit button that does nothing and a
   * second press that stands the player up they were never crouched.
   */
  private clearHeld(): void {
    this.down.clear();
    this.touch?.release();
  }

  /**
   * What the player is actually using. A trackpad clipped onto an iPad reports
   * `mouse` and a finger reports `touch`, and the difference is what decides
   * whether a refused pointer lock is recoverable — see `answerLock`.
   */
  private onAnyPointer = (e: PointerEvent) => {
    this.lastPointerType = e.pointerType;
  };

  /**
   * The escape hatch, and the reason this listener exists at all: a click is a
   * user gesture, so it is the one moment the browser will reliably hand the
   * pointer back. Without it a refused lock is unrecoverable — the player is
   * standing in the game unable to turn, and clicking, which is the first thing
   * anyone tries, did nothing.
   */
  private onCanvasClick = () => {
    // There is nothing to lock on a touch device, and asking would either throw
    // or hand back a promise that never settles. See `requestLock`.
    if (this.touchMode) return;
    if (!this.locked) void this.requestLock();
  };

  /** The browser refusing outright. Treated the same as any other loss. */
  private onLockError = () => {
    this.locked = false;
    this.clearHeld();
  };

  constructor(canvas: HTMLElement, touchLayer?: TouchLayerFactory) {
    this.canvas = canvas;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('pointerlockchange', this.onLockChange);
    document.addEventListener('pointerlockerror', this.onLockError);
    canvas.addEventListener('click', this.onCanvasClick);

    // Everything below this line is the touch path, and a machine that cannot
    // be touched never enters it: no listener, no media query, no module.
    this.touchCapable = hasTouchHardware() || forcedTouchMode() === true;
    if (this.touchCapable) {
      this.makeTouchLayer = touchLayer ?? null;
      window.addEventListener('pointerdown', this.onAnyPointer, { passive: true });
      this.unwatchTouch = watchTouchPreference(this.reconsiderTouch);
      this.reconsiderTouch();
    }
  }

  isDown(action: ActionName): boolean {
    return this.down.has(action);
  }

  pressed(action: ActionName): boolean {
    return this.justPressed.has(action);
  }

  /* ---------------------------------------------------------------- *
   * InputSink — the seam every non-keyboard device writes through
   * ---------------------------------------------------------------- */

  get lookScale(): number {
    return this.sensitivity;
  }

  /**
   * True while the on-screen controls are the ones driving the game.
   *
   * Nothing reads it yet. It is here for `game.ts`, which currently has no way
   * to ask — see the request about `resume()` in the batch notes; outside this
   * file, `touchPreferred()` from `core/touch.ts` answers the same question.
   */
  get touching(): boolean {
    return this.touchMode;
  }

  axes(x: number, z: number): void {
    const ext = this.ext ?? (this.ext = { x: 0, z: 0 });
    ext.x = x;
    ext.z = z;
  }

  look(dx: number, dy: number): void {
    this.lookX += dx;
    this.lookY += dy * (this.invertY ? -1 : 1);
  }

  hold(action: ActionName, down: boolean): void {
    if (down) {
      this.down.add(action);
      this.justPressed.add(action);
    } else {
      this.down.delete(action);
    }
  }

  tap(action: ActionName): void {
    this.down.add(action);
    this.justPressed.add(action);
    this.taps.push(action);
  }

  /**
   * Ask for the pointer back. Resolves false when the browser refused, which is
   * ordinary rather than exceptional: Chrome blocks the request for about a
   * second after the user themselves pressed Escape to leave the lock.
   *
   * The caller has to act on a false. Resuming the game without the pointer
   * leaves the player unable to look around, with no menu up and nothing on
   * screen explaining why — which is exactly the state this used to strand
   * people in.
   */
  requestLock(): Promise<boolean> {
    // On touch there is no pointer to take and nothing that needs one: looking
    // around is a drag on the right half of the screen. Answering false here —
    // which is what an iOS Safari with no Pointer Lock API at all would
    // otherwise produce — sends `Game.resume` straight back to the pause menu
    // on every attempt, so the game would never start on an iPad.
    if (this.touchMode) return Promise.resolve(true);
    const answer = this.askForLock();
    // Desktop gets the promise it always got, unwrapped. Only a machine that
    // has a touchscreen pays for the fallback path below.
    return this.touchCapable ? answer.then((ok) => this.answerLock(ok)) : answer;
  }

  /**
   * A refused lock, reconsidered.
   *
   * The dangerous case is a device with a touchscreen whose browser advertises
   * `requestPointerLock` and then will not grant it. Desktop controls with no
   * pointer lock are no controls at all, and the player is left tapping a
   * pause menu that will not stay closed. If the last thing they touched the
   * machine with was a finger, the on-screen controls are the answer, and the
   * resume they asked for is allowed to go ahead.
   *
   * Deliberately not fired for a mouse: Chrome refuses the lock for about a
   * second after the player pressed Escape themselves, and a laptop with a
   * touchscreen must not flip into thumb controls because of it.
   */
  private answerLock(ok: boolean): boolean {
    if (ok || this.lastPointerType !== 'touch') return ok;
    this.setTouchMode(true);
    return true;
  }

  private askForLock(): Promise<boolean> {
    // Asking for the lock back cancels any release still in flight.
    this.selfReleasing = false;
    const el = this.canvas as HTMLCanvasElement;
    if (typeof el.requestPointerLock !== 'function') return Promise.resolve(false);
    if (document.pointerLockElement === el) return Promise.resolve(true);
    try {
      const pending = el.requestPointerLock() as unknown as Promise<void> | undefined;
      if (pending && typeof pending.then === 'function') {
        // Raced, not awaited. A promise that resolves or rejects is the answer;
        // one that does nothing is the caller left hanging, and the caller here
        // is `Game.resume`, whose whole job is to put the pause menu back when
        // the answer is no. A handler that never runs is the unrecoverable
        // state this method exists to prevent, arrived at from the other side.
        return Promise.race([
          pending.then(
            () => true,
            () => false
          ),
          this.lockTimeout(el),
        ]);
      }
      // Older builds return nothing and report the outcome through events.
      return new Promise<boolean>((resolve) => {
        const settle = (ok: boolean): void => {
          document.removeEventListener('pointerlockchange', onChange);
          document.removeEventListener('pointerlockerror', onError);
          clearTimeout(timer);
          resolve(ok);
        };
        const onChange = (): void => settle(document.pointerLockElement === el);
        const onError = (): void => settle(false);
        document.addEventListener('pointerlockchange', onChange);
        document.addEventListener('pointerlockerror', onError);
        // Neither event is guaranteed to arrive; never leave the caller hanging.
        const timer = setTimeout(() => settle(document.pointerLockElement === el), LOCK_TIMEOUT_MS);
      });
    } catch {
      return Promise.resolve(false);
    }
  }

  /** Resolves to whatever the lock state actually is once the wait is up. */
  private lockTimeout(el: Element): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      setTimeout(() => resolve(document.pointerLockElement === el), LOCK_TIMEOUT_MS);
    });
  }

  releaseLock(): void {
    if (document.pointerLockElement !== this.canvas) return;
    this.selfReleasing = true;
    document.exitPointerLock();
  }

  endFrame(): void {
    this.justPressed.clear();
    this.lookX = 0;
    this.lookY = 0;
    // Empty on every desktop frame. A tap is down for exactly the frame that
    // saw it, which is how a mouse click already behaves to everything that
    // reads `isDown`.
    for (let i = 0; i < this.taps.length; i++) this.down.delete(this.taps[i]!);
    this.taps.length = 0;
  }

  /** Called by the game loop before reading axes. */
  beginFrame(): void {
    this.moveX = (this.isDown('right') ? 1 : 0) - (this.isDown('left') ? 1 : 0);
    this.moveZ = (this.isDown('forward') ? 1 : 0) - (this.isDown('back') ? 1 : 0);
    // Null unless something analogue has ever pushed. Per-axis rather than
    // wholesale so a device that only supplies one of them leaves the other
    // to the keyboard.
    const ext = this.ext;
    if (ext === null) return;
    if (ext.x !== 0) this.moveX = ext.x;
    if (ext.z !== 0) this.moveZ = ext.z;
  }

  setSensitivity(v: number): void {
    this.sensitivity = v;
  }

  setInvertY(v: boolean): void {
    this.invertY = v;
  }

  /* ---------------------------------------------------------------- *
   * Touch mode
   * ---------------------------------------------------------------- */

  private reconsiderTouch = (): void => {
    this.setTouchMode(touchPreferred());
  };

  /**
   * Turn the on-screen controls on or off.
   *
   * The factory is asked here and nowhere else, and it is only ever non-null on
   * a machine with touch hardware — so on a desktop the module that draws the
   * controls is a chunk the browser never asks the server for. A factory that
   * fails is swallowed: no controls is bad, and a thrown module that takes the
   * game's constructor down with it is worse.
   */
  private setTouchMode(on: boolean): void {
    if (on === this.touchMode) return;
    this.touchMode = on;
    // The stylesheet is loaded either way; every touch rule in it is gated on
    // this class, so a desktop matches none of them.
    document.documentElement.classList.toggle('touch-ui', on);
    const token = ++this.touchToken;
    if (!on) {
      this.touch?.release();
      this.touch?.dispose();
      this.touch = null;
      // Hand the axes back to the keyboard, and drop anything a thumb was
      // holding when the trackpad appeared.
      this.ext = null;
      this.down.clear();
      return;
    }
    void this.makeTouchLayer?.(this)
      .then((layer) => {
        // The token catches a layer that arrives after the mode has flipped
        // back — a Magic Keyboard clipped on while the module was in flight —
        // and disposes it rather than installing it over a desktop.
        if (!layer) return;
        if (token !== this.touchToken || !this.touchMode) {
          layer.dispose();
          return;
        }
        this.touch = layer;
      })
      .catch(() => {
        /* Nothing useful to say and nobody to say it to. */
      });
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('pointerlockchange', this.onLockChange);
    document.removeEventListener('pointerlockerror', this.onLockError);
    this.canvas.removeEventListener('click', this.onCanvasClick);

    if (!this.touchCapable) return;
    window.removeEventListener('pointerdown', this.onAnyPointer);
    this.unwatchTouch?.();
    this.unwatchTouch = null;
    // Bumped so a HUD still in flight from a dynamic import is discarded rather
    // than installed against a disposed manager on a dev-server reload.
    this.touchToken++;
    this.touch?.dispose();
    this.touch = null;
    this.touchMode = false;
    document.documentElement.classList.remove('touch-ui');
  }
}

/** Concrete type so the game loop can call `beginFrame`. */
export type InputManager = InputAPI & { beginFrame(): void };

/**
 * The manager as this file actually builds it.
 *
 * `InputSink` and `touching` are deliberately kept out of `InputManager`.
 * That type is implemented by decorators elsewhere — `render/xrinput.ts`
 * wraps one — and every member added to it is a member they have to grow too.
 * A wider *return* type costs nobody anything: a caller that wants the sink
 * has it, and a caller that annotates the field `InputManager` never sees it.
 */
export function makeInput(
  canvas: HTMLElement,
  touchLayer?: TouchLayerFactory
): InputManager & InputSink & { readonly touching: boolean } {
  return new Input(canvas, touchLayer);
}
